import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Building2, 
  Star, 
  Phone, 
  Mail, 
  MapPin,
  User,
  FileText,
  Search,
  StarHalf,
  Merge,
  TrendingUp,
  TrendingDown,
  Calendar,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { fmtDateTime } from '../lib/datetime';
import { formatCuit, normalizeCuit, cuitWarning } from '../lib/cuit';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (user?.role === 'admin') return true;
  return user?.permissions?.[module]?.[action] === true;
};

function Suppliers() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const runImport = async (commit) => {
    if (!importFile) { toast.error('Seleccioná un archivo .xlsx'); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('dry_run', commit ? 'false' : 'true');
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/import`, fd, { withCredentials: true });
      setImportPreview(r.data);
      if (commit) {
        toast.success(`Importación completa: ${r.data.created_ids?.length || 0} creados, ${r.data.updated_ids?.length || 0} actualizados`);
        fetchSuppliers();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la importación');
    } finally { setImportBusy(false); }
  };

  const closeImport = () => { setImportOpen(false); setImportFile(null); setImportPreview(null); };
  
  const [formData, setFormData] = useState({
    business_name: '',
    tax_id: '',
    trade_name: '',
    address: '',
    city: '',
    province: '',
    legacy_code: '',
    phone: '',
    email: '',
    contact_name: '',
    category: '',
    tax_condition: '',
    observations: '',
    registration_date: ''
  });

  // v3.38.0 — Imputación contable por defecto (SOLO Otras Compras)
  const [defaultImp, setDefaultImp] = useState({
    imputation_type: 'none', // 'none' | 'account' | 'concept'
    imputation_account_code: '',
    expense_concept_id: '',
  });
  const [initialDefaultImp, setInitialDefaultImp] = useState({ imputation_type: 'none', imputation_account_code: '', expense_concept_id: '' });
  const [defaultImpMeta, setDefaultImpMeta] = useState({ updated_at: null, updated_by_name: null });
  const [accounts, setAccounts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);

  // Permisos
  const canCreate = userCan(user, 'proveedores', 'crear');
  const canEdit = userCan(user, 'proveedores', 'editar');
  const canDelete = userCan(user, 'proveedores', 'eliminar');

  useEffect(() => {
    fetchSuppliers();
    // v3.38.0 — Cargar maestros para el picker de imputación por defecto
    (async () => {
      try { const r = await axios.get(`${BACKEND_URL}/api/accounting/accounts?only_postable=true`, { withCredentials: true }); setAccounts(r.data || []); } catch { /* opcional */ }
      try { const r = await axios.get(`${BACKEND_URL}/api/treasury/expense-concepts`, { withCredentials: true }); setExpenseConcepts(r.data || []); } catch { /* opcional */ }
    })();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/suppliers`, { withCredentials: true });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingSupplier(null);
    setFormData({
      business_name: '',
      tax_id: '',
      trade_name: '',
      address: '',
      city: '',
      province: '',
      legacy_code: '',
      phone: '',
      email: '',
      contact_name: '',
      category: '',
      tax_condition: '',
      observations: '',
      registration_date: ''
    });
    setDefaultImp({ imputation_type: 'none', imputation_account_code: '', expense_concept_id: '' });
    setInitialDefaultImp({ imputation_type: 'none', imputation_account_code: '', expense_concept_id: '' });
    setDefaultImpMeta({ updated_at: null, updated_by_name: null });
    setDialogOpen(true);
  };

  const handleOpenEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      business_name: supplier.business_name || '',
      tax_id: supplier.tax_id || '',
      trade_name: supplier.trade_name || '',
      address: supplier.address || '',
      city: supplier.city || '',
      province: supplier.province || '',
      legacy_code: supplier.legacy_code || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      contact_name: supplier.contact_name || '',
      category: supplier.category || '',
      tax_condition: supplier.tax_condition || '',
      observations: supplier.observations || '',
      registration_date: supplier.registration_date || ''
    });
    const dImp = {
      imputation_type: supplier.default_imputation_type || 'none',
      imputation_account_code: supplier.default_imputation_account_code || '',
      expense_concept_id: supplier.default_expense_concept_id || '',
    };
    setDefaultImp(dImp);
    setInitialDefaultImp(dImp);
    setDefaultImpMeta({
      updated_at: supplier.default_imputation_updated_at || null,
      updated_by_name: supplier.default_imputation_updated_by_name || null,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let supplierId = editingSupplier?.supplier_id;
      if (editingSupplier) {
        await axios.put(
          `${BACKEND_URL}/api/suppliers/${editingSupplier.supplier_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Proveedor actualizado');
      } else {
        const r = await axios.post(
          `${BACKEND_URL}/api/suppliers`,
          formData,
          { withCredentials: true }
        );
        supplierId = r.data?.supplier_id || supplierId;
        toast.success('Proveedor creado');
      }

      // v3.38.0 — Guardar/limpiar imputación por defecto si cambió
      const changed = defaultImp.imputation_type !== initialDefaultImp.imputation_type
        || defaultImp.imputation_account_code !== initialDefaultImp.imputation_account_code
        || defaultImp.expense_concept_id !== initialDefaultImp.expense_concept_id;
      if (supplierId && changed) {
        // Validación mínima en el cliente
        if (defaultImp.imputation_type === 'account' && !defaultImp.imputation_account_code) {
          toast.error('Elegí la cuenta contable por defecto o cambialo a "Sin default"');
          return;
        }
        if (defaultImp.imputation_type === 'concept' && !defaultImp.expense_concept_id) {
          toast.error('Elegí el concepto de gasto por defecto o cambialo a "Sin default"');
          return;
        }
        try {
          await axios.patch(
            `${BACKEND_URL}/api/suppliers/${supplierId}/default-imputation`,
            {
              imputation_type: defaultImp.imputation_type,
              imputation_account_code: defaultImp.imputation_type === 'account' ? defaultImp.imputation_account_code : null,
              expense_concept_id: defaultImp.imputation_type === 'concept' ? defaultImp.expense_concept_id : null,
            },
            { withCredentials: true }
          );
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se pudo guardar la imputación por defecto');
          return;
        }
      }

      setDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/suppliers/${confirmDelete.supplier_id}`, { withCredentials: true });
      toast.success('Proveedor eliminado');
      setConfirmDelete(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar proveedor');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (supplier) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/suppliers/${supplier.supplier_id}`,
        { is_active: !supplier.is_active },
        { withCredentials: true }
      );
      toast.success(supplier.is_active ? 'Proveedor desactivado' : 'Proveedor activado');
      fetchSuppliers();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleViewDetail = async (supplier) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/suppliers/${supplier.supplier_id}`,
        { withCredentials: true }
      );
      setSelectedSupplier(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingBadge = (rating) => {
    if (rating >= 4) return 'bg-green-100 text-green-800';
    if (rating >= 3) return 'bg-yellow-100 text-yellow-800';
    if (rating > 0) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-600';
  };

  const filteredSuppliers = suppliers.filter(s => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const digits = normalizeCuit(q);
    return (
      s.business_name?.toLowerCase().includes(q) ||
      s.trade_name?.toLowerCase().includes(q) ||
      (digits && normalizeCuit(s.tax_id).includes(digits)) ||
      (s.merged_aliases || []).some(a =>
        a.business_name?.toLowerCase().includes(q) || (digits && normalizeCuit(a.tax_id).includes(digits)))
    );
  });

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Proveedores</h1>
          <p className="text-muted-foreground">Gestión de proveedores y evaluaciones</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={() => navigate('/suppliers/merge')} data-testid="merge-suppliers-button">
                <Merge className="w-4 h-4 mr-2" />
                Fusionar duplicados
              </Button>
            )}
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-supplier-button">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button onClick={handleOpenCreate} data-testid="new-supplier-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proveedor
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Proveedores</p>
                <p className="text-2xl font-bold">{suppliers.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-2xl font-bold text-green-600">{suppliers.filter(s => s.is_active).length}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inactivos</p>
                <p className="text-2xl font-bold text-red-600">{suppliers.filter(s => !s.is_active).length}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Puntaje Promedio</p>
                <p className="text-2xl font-bold">
                  {suppliers.filter(s => s.total_evaluations > 0).length > 0
                    ? (suppliers.filter(s => s.total_evaluations > 0).reduce((acc, s) => acc + s.average_rating, 0) / suppliers.filter(s => s.total_evaluations > 0).length).toFixed(1)
                    : '-'}
                </p>
              </div>
              <Star className="w-8 h-8 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, CUIT..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-suppliers"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>CUIT</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Rubro</TableHead>
                <TableHead className="text-center">Evaluaciones</TableHead>
                <TableHead className="text-center">Puntaje</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredSuppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No hay proveedores registrados
                  </TableCell>
                </TableRow>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <TableRow key={supplier.supplier_id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewDetail(supplier)}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{supplier.business_name}</span>
                        {supplier.trade_name && (
                          <span className="text-sm text-muted-foreground block">{supplier.trade_name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{formatCuit(supplier.tax_id)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {supplier.contact_name && <span className="block">{supplier.contact_name}</span>}
                        {supplier.phone && <span className="text-muted-foreground block">{supplier.phone}</span>}
                      </div>
                    </TableCell>
                    <TableCell>{supplier.category || '-'}</TableCell>
                    <TableCell className="text-center">
                      {supplier.total_evaluations > 0 ? (
                        <Badge variant="outline">{supplier.total_evaluations}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {supplier.total_evaluations > 0 ? (
                        <Badge className={getRatingBadge(supplier.average_rating)}>
                          <Star className="w-3 h-3 mr-1" />
                          {supplier.average_rating.toFixed(1)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Sin evaluar</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={supplier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        onClick={(e) => { e.stopPropagation(); canEdit && handleToggleActive(supplier); }}
                      >
                        {supplier.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(supplier)} data-testid="edit-supplier-button">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(supplier)} data-testid="delete-supplier-button">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="business_name">Razón Social *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  required
                  data-testid="supplier-business-name"
                />
              </div>
              <div>
                <Label htmlFor="tax_id">CUIT/RUT *</Label>
                <Input
                  id="tax_id"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                  placeholder="XX-XXXXXXXX-X"
                  required
                  data-testid="supplier-tax-id"
                />
                {cuitWarning(formData.tax_id) ? (
                  <p className="mt-1 text-xs text-amber-700" data-testid="supplier-cuit-warning">
                    ⚠ {cuitWarning(formData.tax_id)}
                  </p>
                ) : normalizeCuit(formData.tax_id).length === 11 ? (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="supplier-cuit-ok">
                    Se guarda como {formatCuit(formData.tax_id)}
                  </p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="trade_name">Nombre Comercial</Label>
                <Input
                  id="trade_name"
                  value={formData.trade_name}
                  onChange={(e) => setFormData({...formData, trade_name: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="registration_date">Fecha de Alta</Label>
                <Input
                  id="registration_date"
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => setFormData({...formData, registration_date: e.target.value})}
                  data-testid="supplier-registration-date"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="city">Localidad</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  data-testid="supplier-city"
                />
              </div>
              <div>
                <Label htmlFor="province">Provincia</Label>
                <Input
                  id="province"
                  value={formData.province}
                  onChange={(e) => setFormData({...formData, province: e.target.value})}
                  data-testid="supplier-province"
                />
              </div>
              <div>
                <Label htmlFor="legacy_code">Código (sistema anterior)</Label>
                <Input
                  id="legacy_code"
                  value={formData.legacy_code}
                  onChange={(e) => setFormData({...formData, legacy_code: e.target.value})}
                  placeholder="Opcional"
                  data-testid="supplier-legacy-code"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="contact_name">Nombre de Contacto</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="category">Rubro/Categoría</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="Ej: Medicamentos, Insumos, etc."
                />
              </div>
              <div>
                <Label htmlFor="tax_condition">Condición IVA</Label>
                <select
                  id="tax_condition"
                  value={formData.tax_condition}
                  onChange={(e) => setFormData({...formData, tax_condition: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Seleccionar...</option>
                  <option value="responsable_inscripto">Responsable Inscripto</option>
                  <option value="monotributista">Monotributista</option>
                  <option value="exento">Exento</option>
                  <option value="consumidor_final">Consumidor Final</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="observations">Observaciones</Label>
                <textarea
                  id="observations"
                  value={formData.observations}
                  onChange={(e) => setFormData({...formData, observations: e.target.value})}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={3}
                />
              </div>

              {/* v3.38.0 — Imputación contable por defecto (SOLO Otras Compras) */}
              <div className="md:col-span-2 rounded-md border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="supplier-default-imputation-block">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Cuenta de imputación por defecto <span className="text-slate-500 font-normal">(Otras Compras)</span>
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Se usa como <b>sugerencia</b> al cargar una Otra Compra de este proveedor. Las compras con recepción de stock se imputan igual a <b>Bienes de Cambio</b> (no se ve afectado).
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input type="radio" name="default-imp" checked={defaultImp.imputation_type === 'none'}
                      onChange={() => setDefaultImp({ imputation_type: 'none', imputation_account_code: '', expense_concept_id: '' })}
                      data-testid="supplier-default-imp-none" />
                    Sin default
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="radio" name="default-imp" checked={defaultImp.imputation_type === 'account'}
                      onChange={() => setDefaultImp({ ...defaultImp, imputation_type: 'account', expense_concept_id: '' })}
                      data-testid="supplier-default-imp-account" />
                    Cuenta contable
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="radio" name="default-imp" checked={defaultImp.imputation_type === 'concept'}
                      onChange={() => setDefaultImp({ ...defaultImp, imputation_type: 'concept', imputation_account_code: '' })}
                      data-testid="supplier-default-imp-concept" />
                    Concepto de gasto
                  </label>
                </div>
                {defaultImp.imputation_type === 'account' && (
                  <select value={defaultImp.imputation_account_code}
                    onChange={(e) => setDefaultImp({ ...defaultImp, imputation_account_code: e.target.value })}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="supplier-default-account-select">
                    <option value="">Seleccionar cuenta…</option>
                    {accounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                  </select>
                )}
                {defaultImp.imputation_type === 'concept' && (
                  <select value={defaultImp.expense_concept_id}
                    onChange={(e) => setDefaultImp({ ...defaultImp, expense_concept_id: e.target.value })}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="supplier-default-concept-select">
                    <option value="">Seleccionar concepto…</option>
                    {expenseConcepts.map(ec => <option key={ec.concept_id} value={ec.concept_id}>{ec.name}</option>)}
                  </select>
                )}
                {defaultImpMeta.updated_by_name && (
                  <p className="text-[11px] text-muted-foreground" data-testid="supplier-default-imp-meta">
                    Última modificación: {defaultImpMeta.updated_by_name}
                    {defaultImpMeta.updated_at && (<> · {fmtDateTime(defaultImpMeta.updated_at)}</>)}
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-supplier-button">
                {editingSupplier ? 'Guardar Cambios' : 'Crear Proveedor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedSupplier?.business_name}
            </DialogTitle>
            <DialogDescription>
              {selectedSupplier?.trade_name && `${selectedSupplier.trade_name} • `}
              CUIT: {formatCuit(selectedSupplier?.tax_id)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSupplier && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="evaluations">
                  Evaluaciones ({selectedSupplier.evaluations?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Rating Card */}
                  <Card className="col-span-2">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Puntaje Promedio</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-4xl font-bold ${getRatingColor(selectedSupplier.average_rating)}`}>
                              {selectedSupplier.total_evaluations > 0 ? selectedSupplier.average_rating.toFixed(1) : '-'}
                            </span>
                            <div className="flex">
                              {[1,2,3,4,5].map(i => (
                                <Star 
                                  key={i} 
                                  className={`w-5 h-5 ${i <= Math.round(selectedSupplier.average_rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Basado en {selectedSupplier.total_evaluations} evaluación(es)
                          </p>
                        </div>
                        <Badge className={selectedSupplier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {selectedSupplier.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Contact Info */}
                  <div className="space-y-3">
                    {selectedSupplier.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-1" />
                        <span>{selectedSupplier.address}</span>
                      </div>
                    )}
                    {selectedSupplier.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedSupplier.phone}</span>
                      </div>
                    )}
                    {selectedSupplier.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedSupplier.email}</span>
                      </div>
                    )}
                    {selectedSupplier.contact_name && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedSupplier.contact_name}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Info */}
                  <div className="space-y-3">
                    {selectedSupplier.category && (
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>Rubro: {selectedSupplier.category}</span>
                      </div>
                    )}
                    {selectedSupplier.tax_condition && (
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>IVA: {selectedSupplier.tax_condition.replace('_', ' ')}</span>
                      </div>
                    )}
                    {selectedSupplier.registration_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>Alta: {selectedSupplier.registration_date}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedSupplier.observations && (
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-1">Observaciones:</p>
                    <p className="text-sm text-muted-foreground">{selectedSupplier.observations}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="evaluations" className="mt-4">
                {selectedSupplier.evaluations?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedSupplier.evaluations.map((eval_item) => (
                      <Card key={eval_item.evaluation_id}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-medium">OC: {eval_item.order_number}</p>
                              <p className="text-sm text-muted-foreground">
                                Recepción: {eval_item.reception_number} • {fmtDateTime(eval_item.evaluated_at)}
                              </p>
                            </div>
                            <Badge className={getRatingBadge(eval_item.total_score)}>
                              <Star className="w-3 h-3 mr-1" />
                              {eval_item.total_score.toFixed(1)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Conformidad Producto</p>
                              <p className="font-medium">{eval_item.product_conformity}/5</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Cumplimiento Plazos</p>
                              <p className="font-medium">{eval_item.delivery_compliance}/5</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Integridad Pedido</p>
                              <p className="font-medium">{eval_item.order_integrity}/5</p>
                            </div>
                          </div>
                          {eval_item.observations && (
                            <p className="text-sm text-muted-foreground mt-2 italic">"{eval_item.observations}"</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">Evaluado por: {eval_item.evaluator_name}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Este proveedor aún no tiene evaluaciones</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Eliminar proveedor"
        description={<>¿Estás seguro de eliminar el proveedor <strong>{confirmDelete?.business_name}</strong>? Esta acción no se puede deshacer.</>}
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-supplier"
      />

      <Dialog open={importOpen} onOpenChange={(v) => !v && closeImport()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="suppliers-import-dialog">
          <DialogHeader>
            <DialogTitle>Importar proveedores desde Excel</DialogTitle>
            <DialogDescription>
              Subí un archivo .xlsx con columnas <b>Código, Nombre, Domicilio, Teléfono(s), Localidad, Provincia, CUIT, Condición Fiscal IVA</b>.
              Si el CUIT ya existe, se actualizan los datos. Se valida que el CUIT tenga 11 dígitos.
            </DialogDescription>
          </DialogHeader>

          {!importPreview && (
            <div className="space-y-3">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }}
                data-testid="suppliers-import-file"
                className="block text-sm"
              />
              <p className="text-xs text-muted-foreground">El primer paso es una previsualización (&ldquo;dry-run&rdquo;): nada se escribe en la base hasta que confirmes.</p>
            </div>
          )}

          {importPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded border p-2"><p className="text-xs text-muted-foreground">Total filas</p><p className="text-2xl font-bold">{importPreview.total_rows}</p></div>
                <div className="rounded border p-2 bg-green-50"><p className="text-xs text-green-700">A crear</p><p className="text-2xl font-bold text-green-700">{importPreview.to_create}</p></div>
                <div className="rounded border p-2 bg-blue-50"><p className="text-xs text-blue-700">A actualizar</p><p className="text-2xl font-bold text-blue-700">{importPreview.to_update}</p></div>
                <div className="rounded border p-2 bg-red-50"><p className="text-xs text-red-700">Errores</p><p className="text-2xl font-bold text-red-700">{importPreview.errors}</p></div>
              </div>

              {importPreview.errors_detail?.length > 0 && (
                <div className="rounded border p-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium mb-1 text-red-700">Errores (primeros {importPreview.errors_detail.length}):</p>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-muted-foreground"><th>Fila</th><th>CUIT</th><th>Nombre</th><th>Motivo</th></tr></thead>
                    <tbody>{importPreview.errors_detail.map((e, i) => (
                      <tr key={i}><td>{e.row}</td><td>{e.cuit}</td><td className="truncate max-w-[150px]">{e.name}</td><td>{e.reason}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              {importPreview.preview_update?.length > 0 && (
                <div className="rounded border p-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium mb-1 text-blue-700">A actualizar (CUIT ya existe):</p>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-muted-foreground"><th>CUIT</th><th>Nuevo nombre</th><th>Existente</th></tr></thead>
                    <tbody>{importPreview.preview_update.map((r, i) => (
                      <tr key={i}><td>{r.cuit}</td><td>{r.name}</td><td className="text-muted-foreground">{r.existing_name}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              {importPreview.created_ids !== undefined && (
                <div className="rounded border p-3 bg-green-50">
                  <p className="text-sm font-medium text-green-800">
                    ✅ Importación realizada: {importPreview.created_ids.length} creados · {importPreview.updated_ids.length} actualizados
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeImport} disabled={importBusy}>Cerrar</Button>
            {!importPreview && (
              <Button onClick={() => runImport(false)} disabled={importBusy || !importFile} data-testid="suppliers-import-preview">
                {importBusy ? 'Procesando…' : 'Previsualizar'}
              </Button>
            )}
            {importPreview && importPreview.dry_run && (importPreview.to_create + importPreview.to_update) > 0 && (
              <Button onClick={() => runImport(true)} disabled={importBusy} data-testid="suppliers-import-confirm">
                {importBusy ? 'Importando…' : `Confirmar (${importPreview.to_create + importPreview.to_update})`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Suppliers;
